/**
 * Aruba keys store — same shape as meraki-keys, but with the heavier
 * OAuth-flavored payload and a `stale` flag for expired refresh tokens.
 */
import { create } from "zustand";
import {
  get as apiGet,
  post as apiPost,
  patch as apiPatch,
  del as apiDel,
  ApiError,
} from "@/api/client";
import {
  ArubaKeyListSchema,
  type ArubaKey,
  type ArubaKeyMutation,
} from "@/api/schemas/aruba";
import { MOCK_ARUBA_KEYS } from "@/api/fixtures/aruba-keys";
import { useAuth } from "@/stores/auth";

interface State {
  data: ArubaKey[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  load: () => Promise<void>;
  create: (payload: ArubaKeyMutation) => Promise<void>;
  activate: (id: number) => Promise<void>;
  remove: (id: number) => Promise<void>;
  rename: (id: number, name: string) => Promise<void>;
}

export const useArubaKeys = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/aruba/keys/");
      set({ data: ArubaKeyListSchema.parse(raw), status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_ARUBA_KEYS,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  create: async (payload) => {
    await apiPost("/aruba/keys/", payload);
    await get().load();
    void useAuth.getState().bootstrap();
  },

  activate: async (id) => {
    await apiPatch(`/aruba/keys/${id}/`, { active: true });
    set((s) => ({ data: s.data.map((k) => ({ ...k, active: k.id === id })) }));
    await get().load();
    void useAuth.getState().bootstrap();
  },

  remove: async (id) => {
    await apiDel(`/aruba/keys/${id}/`);
    set((s) => ({ data: s.data.filter((k) => k.id !== id) }));
    await get().load();
    void useAuth.getState().bootstrap();
  },

  rename: async (id, name) => {
    await apiPatch(`/aruba/keys/${id}/`, { name });
    set((s) => ({
      data: s.data.map((k) => (k.id === id ? { ...k, name } : k)),
    }));
    await get().load();
  },
}));
